import { participantCalendarAppointmentRecord } from './calendarAppointmentProjection';

import type {
  CalendarActionSubject,
  CalendarAppointmentActionRecord,
  ParticipantCalendarAppointmentActionRecord,
} from './calendarAppointmentProjection';
import type {
  AppointmentScope,
  BrowserAppointment,
  DecodedBrowserAppointment,
  LifecycleStatus,
} from './types';
import type { Session } from '@/types';

import { isSessionActionable } from '@/features/coachSchedule/sessionData';

export type NativeAppointmentActionRecord = CalendarAppointmentActionRecord;
export type AppointmentActionRecord = Session | NativeAppointmentActionRecord;

type ParticipantAppointmentActionRecord<
  TAppointment extends DecodedBrowserAppointment,
> = ParticipantCalendarAppointmentActionRecord & {
  appointment: TAppointment;
};

export type ActionRecordParticipant = {
  avatarUrl: string | null;
  displayName: string;
  personId?: string;
  railsUserId?: string;
};

export const isNativeAppointmentActionRecord = <
  TRecord extends AppointmentActionRecord,
>(
  record: TRecord,
): record is Extract<TRecord, NativeAppointmentActionRecord> =>
  'source' in record;

export const actionRecordRailsSession = (
  record: AppointmentActionRecord,
): Session | undefined => {
  if (!isNativeAppointmentActionRecord(record)) return record;
  return record.railsSession;
};

export const actionRecordSessionSubject = (
  record: AppointmentActionRecord,
): Session => {
  if (!isNativeAppointmentActionRecord(record)) return record;
  const railsSession = actionRecordRailsSession(record);
  if (!railsSession) return record.session;
  return {
    ...record.session,
    user: { ...record.session.user, id: railsSession.user.id },
  };
};

export const actionRecordSubject = (
  record: AppointmentActionRecord,
): CalendarActionSubject =>
  isNativeAppointmentActionRecord(record)
    ? record
    : { session: record, source: 'session' };

export const actionRecordId = (record: AppointmentActionRecord): string =>
  isNativeAppointmentActionRecord(record) ? record.view.id : record.id;

export const actionRecordAppointmentId = (
  record: AppointmentActionRecord,
): string | undefined =>
  isNativeAppointmentActionRecord(record)
    ? record.view.id
    : record.sourceAppointmentId || undefined;

export const actionRecordAppointmentKind = (
  record: AppointmentActionRecord,
): BrowserAppointment['kind'] => {
  if (isNativeAppointmentActionRecord(record)) return record.view.kind;
  return record.isChemistryCall ? 'chemistry' : 'coaching';
};

export const actionRecordKey = (record: AppointmentActionRecord): string =>
  `${isNativeAppointmentActionRecord(record) ? 'appointment' : 'session'}:${actionRecordId(record)}`;

export const actionRecordStartAt = (record: AppointmentActionRecord): string =>
  isNativeAppointmentActionRecord(record)
    ? record.view.currentSchedule.startAt
    : record.startedAt;

export const actionRecordEndAt = (record: AppointmentActionRecord): string =>
  isNativeAppointmentActionRecord(record)
    ? record.view.currentSchedule.endAt
    : record.endedAt;

export const actionRecordTimezone = (
  record: AppointmentActionRecord,
): string | undefined =>
  isNativeAppointmentActionRecord(record)
    ? record.view.currentSchedule.timezone
    : undefined;

export const actionRecordTitle = (record: AppointmentActionRecord): string => {
  if (!isNativeAppointmentActionRecord(record)) return record.title;
  return record.view.kind === 'chemistry'
    ? 'Chemistry Call'
    : 'Coaching Session';
};

export const actionRecordMember = (
  record: AppointmentActionRecord,
): ActionRecordParticipant => {
  if (!isNativeAppointmentActionRecord(record)) {
    return {
      avatarUrl: record.user.avatarUrl ?? null,
      displayName: record.user.fullName,
      personId: '',
      railsUserId: record.user.id,
    };
  }
  const participantIdentity =
    record.source === 'participant-appointment'
      ? record.authoritative.expectedIdentities.member
      : undefined;
  const railsSession = actionRecordRailsSession(record);
  return {
    avatarUrl: participantIdentity?.avatarUrl ?? null,
    displayName: record.view.member.displayName,
    personId: participantIdentity?.personId,
    railsUserId: railsSession?.user.id || undefined,
  };
};

export const actionRecordCoach = (
  record: AppointmentActionRecord,
): ActionRecordParticipant => {
  if (!isNativeAppointmentActionRecord(record)) {
    return {
      avatarUrl: record.coach.avatarUrl ?? null,
      displayName: record.coach.fullName,
      personId: '',
      railsUserId: record.coach.id,
    };
  }
  const participantIdentity =
    record.source === 'participant-appointment'
      ? record.authoritative.expectedIdentities.coach
      : undefined;
  const railsSession = actionRecordRailsSession(record);
  return {
    avatarUrl: participantIdentity?.avatarUrl ?? null,
    displayName: record.view.coach.displayName,
    personId: participantIdentity?.personId,
    railsUserId: railsSession?.coach.id || undefined,
  };
};

export const actionRecordLifecycle = (
  record: AppointmentActionRecord,
): LifecycleStatus | Session['status'] =>
  isNativeAppointmentActionRecord(record)
    ? record.view.lifecycleStatus
    : record.status;

export const actionRecordIsConfirmed = (
  record: AppointmentActionRecord,
): boolean =>
  !isNativeAppointmentActionRecord(record) ||
  record.view.bookingStatus === 'confirmed';

const nativeActionAllowed = (
  record: NativeAppointmentActionRecord,
  action: 'cancel' | 'reschedule',
  scope: AppointmentScope,
): boolean =>
  record.view.lifecycleStatus === 'scheduled' &&
  record.view.actionCapabilities[action].allowed &&
  record.view.actionCapabilities[action].scopes.includes(scope);

export const actionRecordCanCancel = (
  record: AppointmentActionRecord,
  scope: AppointmentScope = 'appointment',
): boolean =>
  isNativeAppointmentActionRecord(record)
    ? nativeActionAllowed(record, 'cancel', scope)
    : (record.coachCancelAvailable ?? isSessionActionable(record));

export const actionRecordCanReschedule = (
  record: AppointmentActionRecord,
  scope: AppointmentScope = 'appointment',
): boolean =>
  isNativeAppointmentActionRecord(record)
    ? nativeActionAllowed(record, 'reschedule', scope)
    : (record.coachRescheduleAvailable ?? isSessionActionable(record));

export const actionRecordAnalyticsIdentity = (
  record: AppointmentActionRecord,
): { appointmentId?: string; bookId?: string; sessionId?: string } => {
  if (!isNativeAppointmentActionRecord(record)) {
    return { bookId: record.bookId, sessionId: record.id };
  }
  const railsSession = actionRecordRailsSession(record);
  return {
    appointmentId: record.view.id,
    ...(railsSession
      ? { bookId: railsSession.bookId, sessionId: railsSession.id }
      : {}),
  };
};

const dedupeSessions = (sessions: Session[]): Session[] => {
  const byId = new Map<string, Session>();
  sessions.forEach((session) => {
    if (!byId.has(session.id)) byId.set(session.id, session);
  });
  return [...byId.values()];
};

const claimedAppointmentId = (session: Session): string | undefined => {
  const sourceId = session.sourceAppointmentId || undefined;
  const bookingId = session.momentsBookingId || undefined;
  if (sourceId && bookingId && sourceId !== bookingId) return undefined;
  return sourceId ?? bookingId;
};

const hasConflictingMarkers = (session: Session): boolean =>
  Boolean(
    session.sourceAppointmentId &&
    session.momentsBookingId &&
    session.sourceAppointmentId !== session.momentsBookingId,
  );

export const reconcileCalendarAppointmentActionRecords = <
  TRecords extends readonly CalendarAppointmentActionRecord[],
>(
  appointmentRecords: TRecords,
  sessions: Session[],
): Array<Session | TRecords[number]> => {
  const uniqueSessions = dedupeSessions(sessions);
  const uniqueAppointmentRecords = new Map<string, TRecords[number]>();
  appointmentRecords.forEach((record) => {
    const current = uniqueAppointmentRecords.get(record.view.id);
    if (!current || record.view.version > current.view.version) {
      uniqueAppointmentRecords.set(record.view.id, record);
    }
  });

  const claims = new Map<string, Session[]>();
  const taintedAppointmentIds = new Set<string>();
  uniqueSessions.forEach((session) => {
    if (hasConflictingMarkers(session)) {
      taintedAppointmentIds.add(session.sourceAppointmentId!);
      taintedAppointmentIds.add(session.momentsBookingId!);
      return;
    }
    const appointmentId = claimedAppointmentId(session);
    if (!appointmentId) return;
    const appointmentClaims = claims.get(appointmentId) ?? [];
    appointmentClaims.push(session);
    claims.set(appointmentId, appointmentClaims);
  });

  const consumedSessionIds = new Set<string>();
  const nativeRecords = [...uniqueAppointmentRecords.values()].map((record) => {
    const matchingSessions = claims.get(record.view.id) ?? [];
    const railsSession =
      matchingSessions.length === 1 &&
      !taintedAppointmentIds.has(record.view.id)
        ? matchingSessions[0]
        : undefined;
    if (!railsSession) return record;
    consumedSessionIds.add(railsSession.id);
    return {
      ...record,
      railsSession,
    };
  });

  const remainingSessions = uniqueSessions
    .filter(({ id }) => !consumedSessionIds.has(id))
    .map((session) => {
      const appointmentId = claimedAppointmentId(session);
      const hasAmbiguousClaim =
        hasConflictingMarkers(session) ||
        Boolean(
          [session.sourceAppointmentId, session.momentsBookingId].some(
            (id) => id && taintedAppointmentIds.has(id),
          ),
        ) ||
        Boolean(appointmentId && (claims.get(appointmentId)?.length ?? 0) > 1);
      return hasAmbiguousClaim
        ? {
            ...session,
            momentsBookingId: undefined,
            sourceAppointmentId: undefined,
          }
        : session;
    });

  return [...nativeRecords, ...remainingSessions].sort((left, right) => {
    const timeDelta =
      Date.parse(actionRecordStartAt(left)) -
      Date.parse(actionRecordStartAt(right));
    if (timeDelta !== 0) return timeDelta;
    return actionRecordKey(left).localeCompare(actionRecordKey(right));
  });
};

export const reconcileAppointmentActionRecords = <
  TAppointment extends DecodedBrowserAppointment,
>(
  appointments: TAppointment[],
  sessions: Session[],
): Array<Session | ParticipantAppointmentActionRecord<TAppointment>> =>
  reconcileCalendarAppointmentActionRecords(
    appointments.map((appointment) => ({
      ...participantCalendarAppointmentRecord(appointment),
      appointment,
    })),
    sessions,
  );
